import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { PageHeader, SectionTitle, Pill } from "@/components/Bits";
import FilesPanel from "@/components/FilesPanel";
import StudioProfile from "@/components/StudioProfile";
import PaymentsStatus from "@/components/PaymentsStatus";
import BillingDataReset from "@/components/BillingDataReset";
import { useAuth } from "@/context/AuthContext";

export function FilesPage() {
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState("");

  useEffect(() => {
    api.get("/projects").then((r) => setProjects(r.data)).catch(() => {});
  }, []);

  return (
    <div className="space-y-10" data-testid="files-page">
      <PageHeader
        overline="Assets"
        title="Files"
        subtitle="Renderings, moodboards, plans, contracts and install photos. Client visibility is off by default."
        actions={
          <select
            data-testid="files-project-filter"
            value={projectId}
            onChange={(e) => setProjectId(e.target.value)}
            className="rounded-sm border border-line bg-white px-3 py-2.5 text-sm text-stone outline-none focus:border-lime"
          >
            <option value="">All projects</option>
            {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        }
      />
      <FilesPanel projectId={projectId || undefined} />
    </div>
  );
}

export function Settings() {
  const { user, isAdmin } = useAuth();
  const [users, setUsers] = useState([]);
  const [google, setGoogle] = useState(null);
  const [calendars, setCalendars] = useState([]);

  const loadGoogle = () =>
    api.get("/integrations/google-calendar/status").then((r) => setGoogle(r.data)).catch(() => setGoogle(null));

  useEffect(() => {
    if (isAdmin) {
      api.get("/auth/users").then((r) => setUsers(r.data)).catch(() => {});
      loadGoogle();
    }
    const flag = new URLSearchParams(window.location.search).get("google");
    if (flag === "connected") toast.success("Google Calendar connected");
    if (flag === "error") toast.error("Google Calendar connection failed — try again");
  }, [isAdmin]);

  const connect = async () => {
    try {
      const { data } = await api.get("/integrations/google-calendar/connect");
      window.location.assign(data.authorization_url);
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const act = async (fn, msg) => {
    try {
      await fn();
      toast.success(msg);
      loadGoogle();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const pickCalendar = async () => {
    try {
      const { data } = await api.get("/integrations/google-calendar/calendars");
      setCalendars(data);
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  return (
    <div className="space-y-10" data-testid="settings-page">
      <PageHeader overline="Account" title="Settings" subtitle="Your profile, roles, integrations and studio access." />

      <div className="card-surface p-7">
        <SectionTitle>Your Profile</SectionTitle>
        <div className="grid gap-6 sm:grid-cols-3">
          {[["Name", user?.name], ["Email", user?.email], ["Role", user?.role]].map(([l, v]) => (
            <div key={l}>
              <div className="overline">{l}</div>
              <div className="mt-2 text-sm text-ink">{v}</div>
            </div>
          ))}
        </div>
      </div>

      {isAdmin && <StudioProfile />}

      {isAdmin && <PaymentsStatus />}

      {isAdmin && <BillingDataReset />}

      {isAdmin && google && (
        <div className="card-surface p-7" data-testid="google-calendar-integration">
          <SectionTitle>Integrations</SectionTitle>
          <div className="flex flex-wrap items-start justify-between gap-6">
            <div>
              <h3 className="font-serif text-xl text-ink">Google Calendar</h3>
              <div className="mt-3 space-y-2 text-sm">
                <div className="flex items-center gap-3">
                  <span className="text-stone">Connection status</span>
                  <Pill testId="google-status-pill" tone={google.connected ? "bg-lime text-ink" : "bg-line text-stone"}>
                    {google.connected ? "Connected" : "Not connected"}
                  </Pill>
                </div>
                {google.account_email && (
                  <div className="text-stone">
                    Connected account <span className="text-ink" data-testid="google-account-email">{google.account_email}</span>
                  </div>
                )}
                {google.connected && (
                  <div className="text-stone">
                    Events go to <span className="text-ink">{google.calendar_name || google.calendar_id}</span>
                  </div>
                )}
              </div>
              {!google.configured && (
                <p className="mt-4 max-w-xl text-xs leading-relaxed text-fog" data-testid="google-setup-hint">
                  {google.setup_hint} Once those two values are in place, this card will let you connect,
                  disconnect or switch Google accounts at any time — nothing is hard-coded.
                </p>
              )}
            </div>
            <div className="flex flex-wrap gap-3">
              {google.connected ? (
                <>
                  <button data-testid="google-manage-btn" onClick={pickCalendar} className="rounded-sm border border-line px-4 py-2.5 text-sm text-ink hover:border-lime">Manage connection</button>
                  <button data-testid="google-sync-btn" onClick={() => act(() => api.post("/integrations/google-calendar/sync"), "Calendar pushed to Google")} className="rounded-sm bg-lime px-4 py-2.5 text-sm font-medium text-ink hover:bg-lime-dark">Sync now</button>
                  <button data-testid="google-disconnect-btn" onClick={() => act(() => api.post("/integrations/google-calendar/disconnect"), "Google Calendar disconnected — your events are untouched")} className="rounded-sm border border-line px-4 py-2.5 text-sm text-stone hover:border-clay hover:text-clay">Disconnect</button>
                </>
              ) : (
                <button
                  data-testid="google-connect-btn"
                  onClick={connect}
                  disabled={!google.configured}
                  className="rounded-sm bg-lime px-5 py-2.5 text-sm font-medium text-ink hover:bg-lime-dark disabled:opacity-50"
                >
                  Connect Google Calendar
                </button>
              )}
            </div>
          </div>

          {google.connected && (
            <div className="mt-7 space-y-4 border-t border-line pt-6">
              <div className="overline">Sync</div>
              {[
                ["sync_to_google", "Asthetik events → Google Calendar"],
                ["sync_from_google", "Google Calendar events → Asthetik (experimental)"],
              ].map(([key, label]) => (
                <label key={key} className="flex items-center gap-3 text-sm text-ink">
                  <input
                    data-testid={`google-${key.replace(/_/g, "-")}`}
                    type="checkbox"
                    checked={!!google[key]}
                    onChange={(e) => act(() => api.patch("/integrations/google-calendar/settings", { [key]: e.target.checked }), "Sync preference saved")}
                    className="h-4 w-4 accent-[#BEC221]"
                  />
                  {label}
                </label>
              ))}
              {calendars.length > 0 && (
                <div data-testid="google-calendar-picker">
                  <div className="overline mb-2">Which Google calendar receives Asthetik events?</div>
                  <select
                    value={google.calendar_id}
                    onChange={(e) => {
                      const cal = calendars.find((c) => c.id === e.target.value);
                      act(() => api.patch("/integrations/google-calendar/settings", { calendar_id: cal.id, calendar_name: cal.name }), "Calendar updated");
                    }}
                    className="rounded-sm border border-line bg-white px-3 py-2 text-sm outline-none focus:border-lime"
                  >
                    {calendars.map((c) => <option key={c.id} value={c.id}>{c.name}{c.primary ? " (primary)" : ""}</option>)}
                  </select>
                </div>
              )}
              <p className="text-xs leading-relaxed text-fog">
                Asthetik stays the record of the studio calendar. Editing an event here updates the same
                Google event rather than creating a second one, and disconnecting Google leaves every
                Asthetik event in place.
              </p>
            </div>
          )}
        </div>
      )}

      {isAdmin && (
        <div>
          <SectionTitle>Studio Users &amp; Roles</SectionTitle>
          <div className="card-surface divide-y divide-line" data-testid="users-list">
            {users.map((u) => (
              <div key={u.id} className="flex flex-wrap items-center justify-between gap-3 px-6 py-4 text-sm">
                <div>
                  <div className="text-ink">{u.name}</div>
                  <div className="text-xs text-fog">{u.email}</div>
                </div>
                <Pill tone={u.role === "admin" ? "bg-lime text-ink" : u.role === "client" ? "bg-[#F6DED6] text-[#A8492E]" : "bg-[#EDEFC4] text-ink"}>{u.role}</Pill>
              </div>
            ))}
          </div>
          <p className="mt-5 max-w-2xl text-xs leading-relaxed text-fog">
            Role permissions are enforced server-side. Client accounts can only reach projects belonging to
            their own client record with the portal enabled, and never see trade cost, markup, margin, profit
            or internal notes.
          </p>
        </div>
      )}
    </div>
  );
}
